import { useQuery } from '@tanstack/react-query';
import { getBranding } from '@/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/** Default Ving logo (used for both header and auth when no branding applies). */
export const DEFAULT_LOGO_SRC = '/assets/images/app-ving-logo.png';

/**
 * Per-network NOVI SSO option for the login page (pre-auth, host-resolved).
 * Present only when the host's network offers NOVI SSO.
 */
export interface NoviSsoOption {
  /** Brand word for the "Continue with {label}" button. */
  label: string;
  /** Uploaded button icon (raw asset), or null to fall back to a neutral glyph. */
  logoUrl: string | null;
  /** Network slug — used to build the /login/novi/{slug}/ URL. */
  slug: string;
}

export interface ResolvedBranding {
  /** Transparent header logo for the top-left app header. */
  headerLogoSrc: string;
  /** 3:1 banner logo for the auth/login pages. */
  authLogoSrc: string;
  /** Whether any network branding is being applied (vs. the default Ving logo). */
  isBranded: boolean;
  /** The host network's NOVI SSO login option, or null when not offered. */
  noviSso: NoviSsoOption | null;
}

/**
 * Resolves which logos to display, reconciling the two branding sources:
 *
 *  - Pre-auth: host branding from GET /api/branding/?host=<hostname> (the only
 *    signal on the login page).
 *  - Post-auth: the active network context's branding from /me, which overrides
 *    host branding once known.
 *
 * Falls back to the default Ving logo whenever no enabled branding applies, and
 * degrades gracefully if /api/branding/ is unavailable.
 */
export function useBranding(): ResolvedBranding {
  // Guard `hostname` access — tests (and some environments) replace
  // window.location with mocks that omit it; never let branding crash a render.
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';

  const { data: hostBranding } = useQuery({
    queryKey: ['branding', host],
    queryFn: () => getBranding(host),
    enabled: host.length > 0,
    staleTime: Infinity, // host branding doesn't change within a session
    retry: false, // missing/unavailable branding → fall back to default, don't retry
  });

  // Active network context branding (post-auth) wins over host branding.
  const { user } = useCurrentUser();
  const network = user?.context?.type === 'network' ? user.context.network : null;

  const hostEnabled = hostBranding?.brandingEnabled ?? false;

  const headerLogoSrc =
    network?.headerLogo?.url ||
    (hostEnabled ? hostBranding?.headerLogo?.url : null) ||
    DEFAULT_LOGO_SRC;

  const authLogoSrc =
    network?.logo?.url ||
    (hostEnabled ? hostBranding?.logo?.url : null) ||
    DEFAULT_LOGO_SRC;

  // NOVI SSO is a pre-auth, host-resolved option (independent of branding). It
  // needs the slug to build the login URL; the backend defaults the label to the
  // slug uppercased, but we guard so the button never reads "Continue with ".
  const noviSso: NoviSsoOption | null =
    hostBranding?.noviSsoEnabled && hostBranding.identifier
      ? {
          label: hostBranding.noviSsoLabel || hostBranding.identifier.toUpperCase(),
          logoUrl: hostBranding.noviSsoLogo?.url ?? null,
          slug: hostBranding.identifier,
        }
      : null;

  return {
    headerLogoSrc,
    authLogoSrc,
    isBranded: headerLogoSrc !== DEFAULT_LOGO_SRC || authLogoSrc !== DEFAULT_LOGO_SRC,
    noviSso,
  };
}
